// @ts-check
// =============================================================================
// js/ui/fullscreen.js — mobile fullscreen for the duel, shared.
//
// WHAT THIS IS (duel.md "Fullscreen on mobile duels"; cross-cutting.md)
//   On coarse-pointer (touch) devices the duel plays fullscreen. Browsers only
//   grant fullscreen inside a user gesture, so the request fires as EARLY as
//   possible: the very click that sends the player into a game — the lobby's
//   Host / Invite / Duel / Load / Spectate buttons and the ladder's Start
//   Duel — calls requestGameFullscreen() synchronously, so the duel screen
//   mounts with fullscreen already up instead of asking for one more tap.
//
//   Gestureless entries still exist (a HOST is socket-pushed into the duel
//   when an opponent takes the seat; reconnects re-enter from 'Duel Update'),
//   and any request can be refused — so the duel screen keeps
//   requestMobileFullscreenOnFirstTap() armed as the fallback.
//
// WHY THE WHOLE PAGE
//   Fullscreen targets documentElement, NOT #duel-stage: the game menu, card
//   zoom, and every modal append to <body> — OUTSIDE the stage — so
//   fullscreening only the stage hid them until you exited. documentElement
//   holds them all, and #duel-stage (fixed inset:0) still fills the screen.
//
//   iPhone Safari has no element-fullscreen API at all — every function here
//   quietly no-ops there (the duel's fixed-inset layout is already
//   viewport-maximized, duel.md's accepted fallback).
// =============================================================================

/** Is fullscreen currently engaged (standard or WebKit-prefixed)? */
function fullscreenActive() {
	return !!(document.fullscreenElement || /** @type {any} */ (document).webkitFullscreenElement);
}

/**
 * The page-wide fullscreen request function, or null where the API is missing
 * (iPhone Safari).
 * @returns {(() => Promise<void>|void)|null}
 */
function pageRequest() {
	const target = document.documentElement;
	return target.requestFullscreen || /** @type {any} */ (target).webkitRequestFullscreen || null;
}

/**
 * Request fullscreen RIGHT NOW — call this synchronously inside the click that
 * sends the player into a game, before any await (a slow network round-trip
 * can outlive the browser's transient-activation window; the gesture is only
 * guaranteed valid while the handler is still on the stack).
 *
 * No-ops on fine-pointer devices, when already fullscreen, and on iPhone
 * Safari. Refusals are swallowed — the duel's first-tap fallback retries.
 * @returns {void}
 */
export function requestGameFullscreen() {
	if (!window.matchMedia('(pointer: coarse)').matches) return;
	if (fullscreenActive()) return;
	const request = pageRequest();
	if (!request) return;
	try {
		const result = request.call(document.documentElement);
		if (result && typeof result.then === 'function') result.catch(() => {});
	} catch {
		// gesture refused — fine, the in-duel fallback will ask again
	}
}

/**
 * Leave fullscreen if it's engaged. Used when the duel screen unmounts, and
 * by the game-entry buttons when the server says no (a failed host/join must
 * not strand the player fullscreen in the lobby).
 * @returns {void}
 */
export function exitGameFullscreen() {
	if (!fullscreenActive()) return;
	const exit = document.exitFullscreen || /** @type {any} */ (document).webkitExitFullscreen;
	if (!exit) return;
	try {
		const result = exit.call(document);
		if (result && typeof result.then === 'function') result.catch(() => {});
	} catch {
		// already out — fine
	}
}

/**
 * Fallback: request fullscreen off a tap inside the duel (duel.md). Covers
 * gestureless entries (hosts, reconnects) and refused early requests.
 *
 * KEEP TRYING until it takes: the old version gave up after the FIRST tap, so
 * a single rejected request (Android Chromium/Brave often refuse the very
 * first gesture, or only honor `click`/`touchend` — not `pointerdown`) left
 * the game stuck with the browser chrome up forever. We listen on both
 * pointerdown AND click and only stop once fullscreen actually engages.
 * @param {HTMLElement} el
 * @returns {void}
 */
export function requestMobileFullscreenOnFirstTap(el) {
	if (!window.matchMedia('(pointer: coarse)').matches) return;
	const request = pageRequest();
	if (!request) return;
	const cleanup = () => {
		el.removeEventListener('pointerdown', tryFullscreen);
		el.removeEventListener('click', tryFullscreen);
	};
	function tryFullscreen() {
		if (fullscreenActive()) {
			cleanup();
			return;
		}
		try {
			const result = request.call(document.documentElement);
			if (result && typeof result.then === 'function') result.then(cleanup, () => {});
		} catch {
			// gesture refused — leave the listeners armed so the next tap retries
		}
	}
	el.addEventListener('pointerdown', tryFullscreen);
	el.addEventListener('click', tryFullscreen);
}
